import React from 'react';
import { Box, ButtonBase, Skeleton, Typography } from '@mui/material';
import { ANALYTICS_FONT_FAMILY } from './analyticsTypography';

const CARD_SHELL_SX = {
  position: 'relative',
  width: '100%',
  textAlign: 'left',
  display: 'block',
  borderRadius: '14px !important',
  border: '1px solid rgba(0,0,0,0.06)',
  bgcolor: '#F5F5F7',
  overflow: 'hidden',
  fontFamily: ANALYTICS_FONT_FAMILY,
};

const INTERACTIVE_CARD_SX = {
  ...CARD_SHELL_SX,
  margin: 0,
  padding: 0,
  minHeight: 0,
  lineHeight: 'normal',
  transition: 'border-color 160ms ease, transform 120ms ease',
  '&:hover': { borderColor: 'rgba(0,0,0,0.1)' },
  '&:active': { transform: 'scale(0.997)' },
  cursor: 'pointer',
  textTransform: 'none',
  '& .MuiTypography-root': { textTransform: 'none' },
};

/** Align card top with Recent Activity on md+ (activity wrapper uses mt: 0). */
const HERO_STRIP_WRAPPER_SX = {
  mb: { xs: 2, sm: 2.5 },
  mt: { xs: 0, md: 0 },
};

const SHOWCASE_BODY_SX = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
};

/** Taller than 16:9 so bottom copy clears the centered image safe zone. */
const IMAGE_STAGE_SX = {
  position: 'relative',
  width: '100%',
  aspectRatio: { xs: '16 / 12.1', sm: '16 / 11' },
  bgcolor: '#F5F5F7',
  overflow: 'hidden',
  boxSizing: 'border-box',
  flexShrink: 0,
};

/** 16:9 image band pinned to top — safe zone position unchanged within this canvas. */
const IMAGE_CANVAS_SX = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  aspectRatio: '16 / 9',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 0,
};

/** Matches 80% image safe zone — used for the loading skeleton. */
const SAFE_ZONE_BOX_SX = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  width: '80%',
  height: '80%',
  transform: 'translate(-50%, -50%)',
  boxSizing: 'border-box',
};

const SAFE_ZONE_SKELETON_SX = {
  ...SAFE_ZONE_BOX_SX,
  borderRadius: '8px',
  zIndex: 1,
};

const IMAGE_GRADIENT_SX = {
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: 0,
  height: { xs: '46%', sm: '50%' },
  background:
    'linear-gradient(to top, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.24) 68%, transparent 100%)',
  pointerEvents: 'none',
  zIndex: 1,
};

const CONTENT_OVERLAY_SX = {
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: { xs: 12, sm: 12 },
  px: { xs: 1.25, sm: 1.5 },
  py: { xs: 1, sm: 1.125 },
  pt: { xs: 1.25, sm: 1.125 },
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: { xs: 0.625, sm: 0.5 },
  textAlign: 'center',
  bgcolor: 'transparent',
  zIndex: 2,
};

const periodLabel = (timeRangeLabel) =>
  timeRangeLabel ? `in the ${timeRangeLabel.toLowerCase()}` : 'in this period';

const floorPlanOfPeriodEyebrow = (timeRangeLabel) => {
  const normalized = (timeRangeLabel || '').toLowerCase();
  if (normalized.includes('24 hour')) return 'FLOOR PLAN OF THE DAY';
  if (normalized.includes('7 day')) return 'FLOOR PLAN OF THE WEEK';
  if (normalized.includes('30 day')) return 'FLOOR PLAN OF THE MONTH';
  return 'FLOOR PLAN OF THE WEEK';
};

/**
 * Page-top hero — featured blank floor plan showcase;
 * text card for reach (visitors) or supply (submissions) fallbacks.
 */
const resolveHeroCopy = ({
  shuffleCount = 0,
  uniqueVisitors = 0,
  submissionsCount = 0,
  timeRangeLabel = null,
}) => {
  const period = periodLabel(timeRangeLabel);

  if (shuffleCount > 0) {
    return { mode: 'exploration' };
  }

  if (uniqueVisitors > 0) {
    return {
      mode: 'reach',
      headline: `${uniqueVisitors.toLocaleString()} people opened staged floor plans`,
      subline: period,
    };
  }

  if (submissionsCount > 0) {
    return {
      mode: 'supply',
      headline: `${submissionsCount.toLocaleString()} floor plan submission${
        submissionsCount !== 1 ? 's' : ''
      }`,
      subline: period,
    };
  }

  return null;
};

const HeroShowcaseImage = ({ name, previewImage, loading }) => {
  const initial = (name || 'F').trim()[0]?.toUpperCase() || 'F';

  if (loading) {
    return (
      <Skeleton
        variant="rectangular"
        aria-hidden="true"
        sx={{
          ...SAFE_ZONE_SKELETON_SX,
          bgcolor: 'rgba(0,0,0,0.06)',
        }}
      />
    );
  }

  if (previewImage) {
    return (
      <Box
        component="img"
        src={previewImage}
        alt={name ? `${name} blank floor plan` : 'Blank floor plan preview'}
        loading="lazy"
        decoding="async"
        sx={{
          position: 'relative',
          zIndex: 0,
          maxWidth: '80%',
          maxHeight: '80%',
          width: 'auto',
          height: 'auto',
          objectFit: 'contain',
          display: 'block',
          boxShadow: '0 2px 8px rgba(0,0,0,0.18), 0 0 1px rgba(0,0,0,0.12)',
          borderRadius: '2px',
        }}
      />
    );
  }

  return (
    <Typography
      sx={{
        fontSize: { xs: '2.5rem', sm: '3rem' },
        fontWeight: 600,
        color: 'rgba(0,0,0,0.28)',
        lineHeight: 1,
        userSelect: 'none',
        fontFamily: 'inherit',
      }}
    >
      {initial}
    </Typography>
  );
};

const ExplorationShowcaseCard = ({
  featuredPlan,
  loading,
  interactive,
  timeRangeLabel = null,
  onClick,
}) => {
  const shuffleCount = featuredPlan?.shuffleCount ?? 0;
  const headline = `${shuffleCount.toLocaleString()} variation${
    shuffleCount !== 1 ? 's' : ''
  } generated`;

  const planName = featuredPlan?.name || 'Floor plan';
  const previewImage = featuredPlan?.previewImage || null;
  const periodEyebrow = floorPlanOfPeriodEyebrow(timeRangeLabel);
  const actionHint = interactive
    ? 'Opens the furnished staging for this floor plan in a new tab'
    : 'Blank floor plan preview only';

  const subline = interactive ? 'Tap to see it furnished' : 'Blank floor plan · preview only';

  const body = (
    <Box sx={SHOWCASE_BODY_SX}>
      <Box sx={IMAGE_STAGE_SX}>
        <Box sx={IMAGE_CANVAS_SX}>
          <HeroShowcaseImage
            name={featuredPlan?.name}
            previewImage={previewImage}
            loading={loading}
          />
        </Box>
        {!loading && featuredPlan && <Box sx={IMAGE_GRADIENT_SX} aria-hidden="true" />}
        {!loading && featuredPlan && (
          <Box sx={CONTENT_OVERLAY_SX}>
            <Typography
              sx={{
                fontSize: { xs: '0.5625rem', sm: '0.625rem' },
                fontWeight: 300,
                lineHeight: 1.35,
                letterSpacing: '0.2em',
                color: 'rgba(255,255,255,0.78)',
                fontFamily: 'inherit',
              }}
            >
              {periodEyebrow}
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: '0.9375rem', sm: '1.0625rem' },
                fontWeight: 600,
                lineHeight: 1.2,
                color: '#fff',
                textShadow: '0 1px 2px rgba(0,0,0,0.25)',
                fontFamily: 'inherit',
              }}
            >
              {headline}
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: '0.6875rem', sm: '0.75rem' },
                fontWeight: 400,
                lineHeight: 1.3,
                color: 'rgba(255,255,255,0.82)',
                fontFamily: 'inherit',
              }}
            >
              {subline}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );

  if (interactive && onClick) {
    return (
      <ButtonBase
        component="div"
        onClick={onClick}
        aria-label={`See furnished staging for ${planName}`}
        sx={INTERACTIVE_CARD_SX}
      >
        {body}
      </ButtonBase>
    );
  }

  return (
    <Box sx={CARD_SHELL_SX} aria-label={actionHint}>
      {body}
    </Box>
  );
};

const TextHeroCard = ({ headline, subline }) => (
  <Box sx={{ ...CARD_SHELL_SX, bgcolor: '#ffffff', p: { xs: 1.75, sm: 2 }, mb: { xs: 2, sm: 2.5 } }}>
    <Typography
      sx={{
        fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.25rem' },
        fontWeight: 600,
        lineHeight: 1.15,
        letterSpacing: '-0.02em',
        color: 'rgba(0,0,0,0.88)',
        fontFamily: 'inherit',
      }}
    >
      {headline}
    </Typography>
    {subline && (
      <Typography
        sx={{
          fontSize: { xs: '0.9375rem', md: '1.0625rem' },
          fontWeight: 400,
          lineHeight: 1.4,
          color: 'rgba(0,0,0,0.55)',
          mt: { xs: 0.5, sm: 0.75 },
          fontFamily: 'inherit',
        }}
      >
        {subline}
      </Typography>
    )}
  </Box>
);

const ShowcaseSkeleton = () => (
  <Box sx={HERO_STRIP_WRAPPER_SX}>
    <Box sx={CARD_SHELL_SX}>
      <Box sx={IMAGE_STAGE_SX}>
        <Box sx={IMAGE_CANVAS_SX}>
          <Skeleton
            variant="rectangular"
            aria-hidden="true"
            sx={{
              ...SAFE_ZONE_SKELETON_SX,
              bgcolor: 'rgba(0,0,0,0.06)',
            }}
          />
        </Box>
      </Box>
    </Box>
  </Box>
);

const EngagementHeroStrip = ({
  loading,
  shuffleCount = 0,
  exploredPlanCount = 0,
  uniqueVisitors = 0,
  submissionsCount = 0,
  timeRangeLabel = null,
  featuredPlan = null,
  featuredPlanPending = false,
  onFeaturedPlanClick = null,
}) => {
  if (loading) {
    return <ShowcaseSkeleton />;
  }

  const copy = resolveHeroCopy({
    shuffleCount,
    uniqueVisitors,
    submissionsCount,
    timeRangeLabel,
  });

  if (!copy) return null;

  if (copy.mode === 'exploration') {
    const showcaseLoading = featuredPlanPending && !featuredPlan;
    const interactive = Boolean(onFeaturedPlanClick);

    return (
      <Box sx={HERO_STRIP_WRAPPER_SX}>
        <ExplorationShowcaseCard
          featuredPlan={featuredPlan}
          loading={showcaseLoading}
          interactive={interactive}
          timeRangeLabel={timeRangeLabel}
          onClick={onFeaturedPlanClick}
        />
      </Box>
    );
  }

  return <TextHeroCard headline={copy.headline} subline={copy.subline} />;
};

export default EngagementHeroStrip;
